import React from 'react';
import Dropdown from 'react-dropdown';
import { connect } from 'react-redux';
import { first } from 'lodash';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import { bindActionCreators } from 'redux';

import createUpdateTimeoff from 'actions/booking/createUpdateTimeoff.action';
import feSetBookingStartTime from 'actions/booking/feSetBookingStartTime.action';
import DatePickerInput from 'components/form/DatePickerInput.component';
import RRPT from 'constants/recurrence/recurrenceRuleParseType.const';
import { withTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonOpeningHours } from 'selectors/currentSalon.selectors';
import { fromPropsToPath } from 'utils/converters/props/path.util';
import constructRecurrenceString from 'utils/recurrence/constructRecurrenceString.util';

import { cleanBookingData, setAllDay, setEndTime, setRepeatForever, setRepeatingRule, setType } from 'booking/actions';
import CategoryTimeOffV1 from 'booking/components/CategoryTimeOffV1.component';
import HeaderWrapper from 'booking/components/common/bw-header/HeaderWrapper';
import { NavTabs } from 'booking/components/common/bw-header/NavTabs';
import BWContent from 'booking/components/common/BWContent';
import BWFooter from 'booking/components/common/BWFooter';
import CloseButton from 'booking/components/common/CloseButton';
import TextField from 'booking/components/common/TextField';
import { closeUrl } from 'booking/utils';
import Checkbox from 'settings/page-elements/dropdown/Checkbox';

import 'react-dropdown/style.css';

const moment = extendMoment(Moment);

class BookTimeOff extends React.Component {
  constructor(props) {
    super(props);

    const startTime = moment(props.startTime).set({
      second: 0,
      millisecond: 0,
    });

    const endTime = props.endTime ? moment(props.endTime) : moment(startTime).add(60, 'minutes');
    const endDate = this.props.endDate ? this.props.endDate : startTime;
    const repeatForever = this.props.forever;

    let notes = '';
    let recurrences = '';
    let existingId = null;

    let existingStylistId = this.props.currentStylistId;

    if (this.props.fetchEvent.response && this.props.fetchEvent.response.id) {
      existingId = this.props.fetchEvent.response.id;
      ({
        fetchEvent: {
          response: { notes, recurrences, stylist_id: existingStylistId },
        },
      } = this.props);
    }

    const openTime = moment(props.openingHours[startTime.day()].open_time, 'HH:mm:ss');
    const closingTime = moment(props.openingHours[startTime.day()].close_time, 'HH:mm:ss');

    const loopTime = openTime.clone();
    const timeRange = [];

    while (!loopTime.isAfter(closingTime)) {
      const currentTime = loopTime.format('HH:mm');
      timeRange.push({
        value: currentTime,
        label: currentTime,
      });
      loopTime.add(15, 'minutes');
    }

    this.state = {
      existingId,
      existingStylistId,
      repeatForever,
      notes,
      endDate,
      repeatRule: this.props.repeatRule,
      recurrences,
      saving: false,
      start_time: startTime,
      end_time: endTime,
      repeatEntry: [
        { value: 'once', label: this.props.t('scheduler.bookTimeOff.optionNoRepeat') },
        { value: 'daily', label: this.props.t('scheduler.bookTimeOff.optionDaily') },
        { value: 'weekly', label: this.props.t('scheduler.bookTimeOff.optionEveryWeek') },
        { value: 'fortnightly', label: this.props.t('scheduler.bookTimeOff.optionEveryTwoWeeks') },
      ],
      timeRange,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.state.saving && nextProps.startedFetch && !nextProps.isFetching && nextProps.errorMessage === '') {
      nextProps.cleanBookingData();
      const newPath = this.props.history.location.pathname
        .split('/')
        .slice(0, this.props.history.location.pathname.includes('/week/') ? 6 : 5)
        .join('/');
      nextProps.history.push(newPath);
    }
  }

  onSubmit = (isEditing) => {
    const { startTime: startTimeFromProps, endTime: endTimeFromProps } = this.props;

    const startTime = moment(startTimeFromProps);
    const endTime = moment(endTimeFromProps);
    if (startTime.isSame(endTime) && !isEditing) return;

    const { repeatRule: rule, recurrences, endDate, repeatForever } = this.state;

    const data = {
      salon_id: this.props.salonId,
      stylist_id: this.props.currentStylistId ? this.props.currentStylistId : this.state.existingStylistId,
      start_time: this.props.allDay ? moment(startTime).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).utc().toISOString() : moment(startTime).utc().toISOString(),
      end_time: this.props.allDay ? moment(endTime).set({ hour: 23, minute: 59, second: 0, millisecond: 0 }).utc().toISOString() : moment(endTime).utc().toISOString(),
      price: 0,
      recurrences: constructRecurrenceString({
        string: recurrences,
        rrs: rule,
        bgn: startTime,
        end: endDate,
        repeatForever,
        type: RRPT.mm,
      }),
      update_all: true,
      status: 'time_off',
      notes: this.state.notes,
    };

    if (this.state.existingId) {
      data.id = this.state.existingId;
      this.props.createUpdateTimeoff(data, 'PUT');
    } else {
      this.props.createUpdateTimeoff(data, 'POST');
    }

    this.setState({ saving: true });

    this.props.cleanBookingData();

    const newPath = first(fromPropsToPath(this.props).split(isEditing ? '/event/edit/' : '/appointment'));

    this.props.history.push(newPath);
  };

  setEndDate = (date) => {
    this.setState({ endDate: date });
  };

  checkAllDay = () => void this.props.setAllDay();

  checkForever = () => {
    this.setState({ repeatForever: !this.state.repeatForever });
  };

  changeStartTime = (data) => {
    const time = moment(data.value, 'HH:mm:ss');
    const startTime = moment(this.state.start_time).set('hour', time.hour()).set('minute', time.minute());
    this.props.feSetBookingStartTime(startTime);
    !startTime.isBefore(this.state.end_time) && this.props.setEndTime(moment(startTime).add(15, 'minutes'));
    this.setState({
      start_time: startTime,
      end_time: startTime.isBefore(this.state.end_time) ? this.state.end_time : moment(startTime).add(15, 'minutes'),
    });
  };

  changeEndTime = (data) => {
    const time = moment(data.value, 'HH:mm:ss');
    const endTime = moment(this.state.start_time).set('hour', time.hour()).set('minute', time.minute());
    this.props.setEndTime(endTime);
    !endTime.isAfter(this.state.start_time) && this.props.feSetBookingStartTime(moment(endTime).subtract(15, 'minutes'));

    this.setState({
      start_time: !endTime.isAfter(this.state.start_time) ? moment(endTime).subtract(15, 'minutes') : this.state.start_time,
      end_time: endTime,
    });
  };

  updateTime = (time, stateValue) => {
    const date = time.clone();
    let startTime = null;
    let endTime = null;
    // validation, start_time cannot be after end_time
    if (stateValue === 'start_time') {
      startTime = date;
      // start time cannot be after end time
      endTime = this.state.end_time.clone();
      if (date.isAfter(endTime)) {
        endTime = moment(startTime).add(15, 'minutes');
      }
    } else if (stateValue === 'end_time') {
      endTime = date;
      startTime = this.state.start_time.clone();
      if (date.isBefore(startTime)) {
        startTime = moment(date).subtract(15, 'minutes');
      }
    } else if (stateValue === 'date') {
      startTime = date;
      endTime = moment(startTime).add(this.state.end_time.diff(this.state.start_time));
    }
    this.props.feSetBookingStartTime(startTime);
    this.props.setEndTime(endTime);
    this.setState({ start_time: startTime, end_time: endTime });
  };

  render() {
    let timeOffOption = this.state.repeatEntry[0];
    switch (this.state.repeatRule) {
      case 'once':
        [timeOffOption] = this.state.repeatEntry;
        break;
      case 'daily':
        [, timeOffOption] = this.state.repeatEntry;
        break;
      case 'weekly':
        [, , timeOffOption] = this.state.repeatEntry;
        break;
      case 'fornightly':
        [, , , timeOffOption] = this.state.repeatEntry;
        break;
      default:
        break;
    }

    const startTime = moment(this.state.start_time, 'HH:mm,ss').format('HH:mm');
    const closeTime = moment(this.state.end_time, 'HH:mm,ss').format('HH:mm');
    const isEditing = this.props.location.pathname.indexOf('edit') > 0;
    const { t } = this.props;

    return (
      <div data-bem="BookTimeOff" className="BookingWindow">
        <HeaderWrapper additionalClass="border-b">
          <NavTabs selected="timeOff" history={this.props.history} setType={this.props.setType} bookedByButton={this.props.bookedByButton} />
          <CloseButton history={this.props.history} disableConfirm close={closeUrl(this.props.location)} />
        </HeaderWrapper>
        <BWContent additionalClass="space">
          <div style={{ marginBottom: 100 }}>
            <div className="pick-time cfix">
              {!isEditing && (
                <label htmlFor="start-time">
                  <div id="start-time">
                    <div className="time-of-picker">
                      <DatePickerInput
                        tag="BookTimeOff__start-time"
                        component={DatePickerInput.ReactDatePickerOld}
                        dateFormat="D MMM YYYY"
                        selected={this.state.start_time}
                        locale="en-GB"
                        onChange={(date) => void this.updateTime(date, 'date')}
                        minDate={this.state.start_time}
                      />
                    </div>
                  </div>
                </label>
              )}
              {!isEditing && (
                <Dropdown
                  className="minuts"
                  controlClassName="react-dropdown"
                  menuClassName="display-bottom"
                  options={this.state.timeRange}
                  onChange={this.changeStartTime}
                  value={this.props.allDay ? { value: '00:00', label: '00:00' } : { value: startTime, label: startTime }}
                  disabled={this.props.allDay}
                  placeholder=""
                />
              )}
              {!isEditing && <span className="divider" />}
              {!isEditing && (
                <Dropdown
                  className="minuts show-r"
                  controlClassName="react-dropdown show-r"
                  menuClassName="display-bottom"
                  options={this.state.timeRange}
                  onChange={this.changeEndTime}
                  value={this.props.allDay ? { value: '00:00', label: '00:00' } : { value: closeTime, label: closeTime }}
                  disabled={this.props.allDay}
                  placeholder=""
                />
              )}
              {!isEditing && (
                <div className="repeat-entry-container cfix">
                  <div className="repeat-entry pull-left">
                    <label htmlFor={this.props.id}>
                      <Checkbox isChecked={this.props.allDay} id={this.props.id} onCheckboxClick={this.checkAllDay} />
                    </label>
                    <span>{t('scheduler.bookTimeOff.checkboxText')}</span>
                  </div>
                  <div className="pull-right">
                    <Dropdown
                      className="time-rule"
                      controlClassName="react-dropdown"
                      menuClassName="display-over"
                      options={this.state.repeatEntry}
                      onChange={(rule) => {
                        this.props.setRepeatingRule(rule.value);
                        this.setState({ repeatRule: rule.value });
                      }}
                      value={timeOffOption}
                      placeholder="Month"
                    />
                  </div>
                </div>
              )}
              {this.state.repeatRule !== 'once' && !isEditing && (
                <div className="repeat-entry-container cfix repeat-entry-forever">
                  <div className="repeat-entry pull-left">
                    <label htmlFor="forever">
                      <Checkbox isChecked={this.state.repeatForever} id="forever" onCheckboxClick={this.checkForever} />
                    </label>
                    <span>{t('scheduler.bookTimeOff.forever')}</span>
                  </div>
                  <div className="pull-right">
                    {!this.state.repeatForever && (
                      <div className="time-of-picker">
                        {t('scheduler.bookTimeOff.until')}
                        <DatePickerInput
                          tag="BookTimeOff__repeat-forever"
                          component={DatePickerInput.ReactDatePickerOld}
                          dateFormat="D MMM YYYY"
                          selected={this.state.endDate ? this.state.endDate : moment()}
                          locale="en-GB"
                          onChange={(date) => void this.setEndDate(date)}
                          minDate={this.state.start_time}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
              <TextField labelName={isEditing ? t('scheduler.bookTimeOff.textareaLabel1') : t('scheduler.bookTimeOff.textareaLabel2')} htmlFor="notes">
                <textarea
                  id="notes"
                  className="note"
                  type="text"
                  onChange={(e) => {
                    this.setState({ notes: e.target.value });
                  }}
                  value={this.state.notes}
                  placeholder={t('scheduler.bookTimeOff.textareaPlaceholder')}
                  style={{ resize: 'vertical' }}
                />
              </TextField>
              <CategoryTimeOffV1 />
            </div>
          </div>
        </BWContent>
        <BWFooter additionalClass="wide-footer sticky-bottom border-t">
          <button className="btn btn-violet" style={{ width: 150 }} onClick={() => this.onSubmit(isEditing)}>
            {isEditing ? t('scheduler.bookTimeOff.saveChangesButton') : t('scheduler.bookTimeOff.bookTimeOffButton')}
          </button>
        </BWFooter>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  isFetching: state.scheduler.isFetching,
  errorMessage: state.booking.errorMessage,
  startedFetch: state.booking.startedFetch,
  salonId: state.currentSalon.id,
  openingHours: selectCurrentSalonOpeningHours(state),
  startTime: state.booking.startTime,
  endTime: state.booking.endTime,
  currentStylistId: state.booking.currentStylistId,
  fetchEvent: state.booking.fetchEvent,
  allDay: state.booking.allDay,
  repeatRule: state.booking.repeat_rule,
  forever: state.booking.forever,
  endDate: state.booking.endDate,
  bookedByButton: state.scheduler.bookedByButton,
  flags: state.flags,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createUpdateTimeoff,
      cleanBookingData,
      feSetBookingStartTime,
      setEndTime,
      setType,
      setAllDay,
      setRepeatingRule,
      setRepeatForever,
    },
    dispatch,
  );

const BookTimeOffHoc = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(BookTimeOff));

export default BookTimeOffHoc;
